// Frontend API client for venue follows.
//
// Public reads (getVenue, getEvents) work for anonymous viewers — the venue
// page renders without auth and the is_following flag stays false.
// Writes (follow/unfollow) and the /following list require auth.

import { API_BASE } from '../config/api';
import AuthService from './authService';
import { getLocalDateString } from '../utils/dateUtils';

const API = `${API_BASE}/api/venues`;

// The viewer's local date, the server's "still on" floor for the lists below.
// Without it the server keeps what is still on SOMEWHERE (a 12h floor), so a
// show that ended last night east of UTC stays listed — and inside a run it
// stays the run's start date and is counted (server/utils/eventDayFloor.js).
const todayParam = () => `today=${getLocalDateString(new Date())}`;

class VenuesService {
  static async getVenue(venueId) {
    try {
      const res = await fetch(`${API}/${encodeURIComponent(venueId)}`, {
        headers: AuthService.getAuthHeaders()
      });
      const data = await AuthService.handleResponse(res);
      return data.venue || null;
    } catch (err) {
      console.error('VenuesService.getVenue:', err);
      return null;
    }
  }

  // Paginated, pre-grouped upcoming shows. One row per show (residencies and
  // tour stops collapsed; one-off events pass through). Returns { shows, hasMore }.
  static async getUpcomingShows(venueId, { limit = 50, offset = 0 } = {}) {
    try {
      const qs = `limit=${limit}&offset=${offset}&${todayParam()}`;
      const res = await fetch(`${API}/${encodeURIComponent(venueId)}/events?${qs}`);
      const data = await AuthService.handleResponse(res);
      return { shows: data.shows || [], hasMore: !!data.hasMore };
    } catch (err) {
      console.error('VenuesService.getUpcomingShows:', err);
      return { shows: [], hasMore: false };
    }
  }

  static async follow(venueId) {
    try {
      const res = await fetch(`${API}/${encodeURIComponent(venueId)}/follow`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders()
      });
      return await AuthService.handleResponse(res);
    } catch (err) {
      console.error('VenuesService.follow:', err);
      return { success: false, error: err.message };
    }
  }

  static async unfollow(venueId) {
    try {
      const res = await fetch(`${API}/${encodeURIComponent(venueId)}/follow`, {
        method: 'DELETE',
        headers: AuthService.getAuthHeaders()
      });
      return await AuthService.handleResponse(res);
    } catch (err) {
      console.error('VenuesService.unfollow:', err);
      return { success: false, error: err.message };
    }
  }

  static async getFollowing() {
    try {
      const res = await fetch(`${API}/following?${todayParam()}`, {
        headers: AuthService.getAuthHeaders()
      });
      const data = await AuthService.handleResponse(res);
      return data.venues || [];
    } catch (err) {
      console.error('VenuesService.getFollowing:', err);
      return [];
    }
  }

  static async search(query, limit = 10) {
    try {
      const res = await fetch(`${API}/search?q=${encodeURIComponent(query)}&limit=${limit}`, {
        headers: AuthService.getAuthHeaders()
      });
      const data = await AuthService.handleResponse(res);
      return data.venues || [];
    } catch (err) {
      console.error('VenuesService.search:', err);
      return [];
    }
  }

  static async getSuggested(limit = 6) {
    try {
      const res = await fetch(`${API}/suggested?limit=${limit}`, {
        headers: AuthService.getAuthHeaders()
      });
      const data = await AuthService.handleResponse(res);
      return data.venues || [];
    } catch (err) {
      console.error('VenuesService.getSuggested:', err);
      return [];
    }
  }
}

export default VenuesService;
